import { Button, Space, Table, Tag, Popconfirm } from "antd";
import { PlusOutlined, EditOutlined, EyeOutlined, DeleteOutlined, FilterOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getJobList, deleteJob } from "../../redux/rtk/features/hiring/hiringSlice";
import HiringJobForm from "./HiringJobForm";
import FilterPanel from "./FilterPanel";
import "./Hiring.css";
import "./FilterPanel.css";

const HiringJobList = () => {
	const dispatch = useDispatch();
	const [formVisible, setFormVisible] = useState(false);
	const [editingJob, setEditingJob] = useState(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [filterStatus, setFilterStatus] = useState("");
	const { jobList, loading } = useSelector((state) => state.hiring);

	useEffect(() => {
		dispatch(getJobList({ page: 1, limit: 50 }));
	}, [dispatch]);

	const handleAdd = () => {
		setEditingJob(null);
		setFormVisible(true);
	};

	const handleEdit = (record) => {
		setEditingJob(record);
		setFormVisible(true);
	};

	const handleFormSuccess = () => {
		setFormVisible(false);
		setEditingJob(null);
		dispatch(getJobList({ page: 1, limit: 50 }));
	};

	const handleDelete = (record) => {
		dispatch(deleteJob(record)).then(() => {
			dispatch(getJobList({ page: 1, limit: 50 }));
		});
	};

	const columns = [
		{
			title: "Title",
			dataIndex: "Title",
			key: "title",
			render: (val, record) => record.Title ?? record.title ?? record.JobTitle ?? record.jobTitle ?? "—",
		},
		{
			title: "Department",
			dataIndex: "departmentName",
			key: "departmentName",
			render: (val, record) =>
				record.departmentName ?? record.DepartmentName ?? record.department?.name ?? "—",
		},
		{
			title: "Designation",
			dataIndex: "designationName",
			key: "designationName",
			render: (val, record) =>
				record.designationName ?? record.DesignationName ?? record.designation?.name ?? "—",
		},
		{
			title: "Headcount",
			dataIndex: "HeadCount",
			key: "headcount",
			render: (val, record) => record.HeadCount ?? record.headcount ?? record.Headcount ?? 1,
			width: 90,
		},
		{
			title: "Status",
			dataIndex: "Status",
			key: "status",
			render: (val, record) => {
				const s = record.Status ?? record.status ?? "Open";
				const color = s === "Closed" ? "default" : "green";
				return <Tag color={color}>{s}</Tag>;
			},
			width: 100,
		},
		{
			title: "Actions",
			key: "action",
			width: 180,
			render: (_, record) => (
				<Space>
					
					<Button
						type="link"
						size="small"
						icon={<EditOutlined />}
						onClick={() => handleEdit(record)}
					>
						Edit
					</Button>
					<Popconfirm
						title="Delete this job opening?"
						description="This will mark the job as deleted."
						onConfirm={() => handleDelete(record)}
						okText="Delete"
						cancelText="Cancel"
						okButtonProps={{ danger: true }}
					>
						<Button type="link" size="small" danger icon={<DeleteOutlined />}>
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	const rawList = jobList || [];
	const filteredList = rawList.filter((item) => {
		const title = (item.Title ?? item.title ?? "").toLowerCase();
		const status = item.Status ?? item.status ?? "";
		if (searchText.trim() && !title.includes(searchText.trim().toLowerCase())) return false;
		if (filterStatus && status !== filterStatus) return false;
		return true;
	});
	const dataSource = filteredList.map((item, index) => ({
		...item,
		key: item.JobOpeningId ?? item.id ?? item.JobId ?? index,
	}));

	return (
		<>
			<div className="flex justify-between items-center mb-4 filter-panel-wrap">
				<span className="text-gray-600">Create and manage job openings.</span>
				<Space>
					<button
						type="button"
						className="filter-panel-trigger"
						onClick={() => setFilterOpen((o) => !o)}
						aria-label="Filters"
					>
						<FilterOutlined style={{ fontSize: 16 }} />
					</button>
					<FilterPanel
						open={filterOpen}
						onClose={() => setFilterOpen(false)}
						search={{
							placeholder: "Search by title, department, designation",
							value: searchText,
							onChange: setSearchText,
						}}
						filters={[
							{
								key: "status",
								label: "Status",
								placeholder: "All Status",
								value: filterStatus,
								onChange: setFilterStatus,
								options: [
									{ value: "", label: "All Status" },
									{ value: "Open", label: "Open" },
									{ value: "Closed", label: "Closed" },
								],
							},
						]}
					/>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={handleAdd}
						className="hiring-btn-green"
					>
						Add job
					</Button>
				</Space>
			</div>
			<Table
				columns={columns}
				dataSource={dataSource}
				loading={loading}
				pagination={{ pageSize: 10 }}
				locale={{ emptyText: "No jobs yet. Add your first job opening." }}
			/>
			<HiringJobForm
				open={formVisible}
				onClose={() => {
					setFormVisible(false);
					setEditingJob(null);
				}}
				onSuccess={handleFormSuccess}
				initialValues={editingJob}
			/>
		</>
	);
};

export default HiringJobList;
